import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, th as palette, I, Wordmark } from "@/lib/bricks";
import { navFor, isActive } from "@/lib/navConfig";
import { useShellDark } from "@/lib/useShellDark";

// One sidebar for every account type (IA in lib/navConfig). Static at ≥1024,
// the drawer below that (DashboardLayout). Icons: precision set, 1.5px, bare.
function NavEntry({ entry, active, p, onNavigate }) {
  const style = {
    display: "flex", alignItems: "center", gap: 12,
    padding: "8px 12px", borderRadius: 2,
    background: active ? p.surf2 : undefined,
    color: active ? p.ink : p.ink2,
    fontSize: 14, lineHeight: "20px", fontWeight: active ? 500 : 400,
    position: "relative", textDecoration: "none",
    transition: "background 0.15s, color 0.15s",
  };
  const testid = `nav-${entry.label.toLowerCase().replace(/\s+/g, "-")}`;
  const body = (
    <>
      {active && <span aria-hidden="true" style={{ position: "absolute", left: 0, top: 8, bottom: 8, width: 2, background: T.brick }} />}
      <I name={entry.icon} size={16} color={active ? p.ink : p.ink3} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.label}</span>
      {entry.external && <I name="external" size={16} color={p.ink4} />}
    </>
  );
  if (entry.external) {
    return (
      <a href={entry.to} onClick={onNavigate} data-testid={testid} className="bx-nav-item" style={style}>
        {body}
      </a>
    );
  }
  return (
    <Link to={entry.to} onClick={onNavigate} data-testid={testid} className="bx-nav-item"
      aria-current={active ? "page" : undefined} style={style}>
      {body}
    </Link>
  );
}

export default function Sidebar({ onNavigate }) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const dark = useShellDark();
  const p = palette(dark);
  const nav = navFor(user);
  const initials = (user?.name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "B";

  return (
    <aside
      data-testid="sidebar"
      aria-label="Main"
      style={{
        width: 248, maxWidth: "85vw", background: p.bg, borderRight: `1px solid ${p.hair}`,
        paddingTop: "max(20px, env(safe-area-inset-top))",
        display: "flex", flexDirection: "column", height: "100%", minHeight: 0, fontFamily: F.display,
      }}
    >
      {/* Wordmark + account */}
      <div style={{ padding: "0 12px 16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 44 }}>
        <Wordmark size={17} color={p.ink} />
        <button
          type="button"
          data-testid="sidebar-close"
          aria-label="Close menu"
          onClick={onNavigate}
          className="inline-flex lg:hidden"
          style={{ background: "none", border: "none", cursor: "pointer", width: 44, height: 44, alignItems: "center", justifyContent: "center", padding: 0 }}
        >
          <I name="x" size={20} color={p.ink2} />
        </button>
      </div>
      <div style={{ padding: "0 24px 16px", borderBottom: `1px solid ${p.hair}` }} aria-label={`Account: ${nav.name}, ${nav.label}`}>
        <div style={{ fontSize: 14, fontWeight: 500, color: p.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nav.name}</div>
        <div style={{ fontSize: 12, color: p.ink3, marginTop: 2 }}>{nav.label}</div>
      </div>

      {/* Nav */}
      <nav style={{ padding: "8px 12px 16px", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
        {nav.sections.map((section, si) => (
          <div key={section.label || si} role="group" aria-label={section.label || "Home"} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {section.label && (
              <div style={{ padding: "16px 12px 4px", fontSize: 12, lineHeight: "16px", color: p.ink4 }}>{section.label}</div>
            )}
            {section.items.map((entry) => (
              <NavEntry key={entry.to} entry={entry} active={isActive(entry, location.pathname)} p={p} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
      </nav>

      {/* Help centre */}
      <div style={{ padding: "8px 12px", borderTop: `1px solid ${p.hair}` }}>
        <a href="https://www.brxs.ca/help" target="_blank" rel="noopener noreferrer" onClick={onNavigate}
          data-testid="nav-help" className="bx-nav-item" aria-label="Help centre (opens in a new tab)"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderRadius: 2, color: p.ink2, fontSize: 14, lineHeight: "20px", textDecoration: "none", minHeight: 44, boxSizing: "border-box" }}>
          <I name="help" size={16} color={p.ink3} />
          <span style={{ flex: 1 }}>Help centre</span>
          <I name="external" size={16} color={p.ink4} />
        </a>
      </div>

      {/* User footer */}
      <div style={{ padding: "12px 12px max(12px, env(safe-area-inset-bottom)) 24px", borderTop: `1px solid ${p.hair}`, display: "flex", alignItems: "center", gap: 12 }}>
        <div aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 2, background: p.surf2, color: p.ink2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 500, flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: p.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name || "Your account"}</div>
          <div style={{ fontSize: 12, color: p.ink3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || nav.label}</div>
        </div>
        <button
          type="button"
          data-testid="logout-btn"
          onClick={logout}
          aria-label="Sign out"
          title="Sign out"
          style={{ background: "none", border: "none", cursor: "pointer", width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, borderRadius: 2 }}
        >
          <I name="logout" size={16} color={p.ink3} />
        </button>
      </div>
    </aside>
  );
}
